import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useId, useMemo, useState } from 'react';
import {
  useAuthUserRestaurant,
  useDenyProductOfferOrderMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';
import { ProductOfferOrder } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { Modal } from '@/components/shared/modal';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';

interface DenyProductOfferOrderModalProps {
  order: ProductOfferOrder;
  onClose: () => unknown;
}

export const DenyProductOfferOrderModal = ({
  order,
  onClose,
}: DenyProductOfferOrderModalProps) => {
  const { t } = useTranslation();
  const uid = useId();
  const restaurant = useAuthUserRestaurant();

  const [reason, setReason] = useState('');

  const denyOrder = useDenyProductOfferOrderMutation();

  const isValid = useMemo(() => reason.trim().length > 0, [reason]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      await denyOrder.mutateAsync({
        restaurant_id: restaurant.id,
        product_offer_order_id: order.id,
        denial_reason: reason.trim(),
      });
      onClose();
      toast.success(t('product-offers.deny-order-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('product-offers.deny-order-error-msg'));
    }
  };

  return (
    <Modal
      isModalOpen
      closeModal={onClose}
      title={t('product-offers.deny-order')}
      description={t('product-offers.deny-order-description', {
        productOffer: order.product_offer.name,
      })}
    >
      <form onSubmit={handleSubmit}>
        <div className="grid gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor={uid + '-reason'}>
              {t('product-offers.denial-reason')}
            </Label>
            <textarea
              required
              rows={4}
              id={uid + '-reason'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button
            type="submit"
            variant="destructive"
            disabled={!isValid}
            isLoading={denyOrder.isPending}
          >
            {t('product-offers.deny')}
          </Button>
        </DialogFooter>
      </form>
    </Modal>
  );
};
